import React, { useState, useEffect } from 'react';
import { Calendar, ChevronRight } from 'lucide-react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { formatDate } from '../../utils/dateUtils';
import { toast } from 'react-hot-toast';
import { XMLParser, XMLValidator } from 'fast-xml-parser';

interface BlogPost {
  title: string;
  link: string;
  pubDate: string;
  description: string;
  'content:encoded': string;
  'content:encodedSnippet': string;
}

export function BlogFeed() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const RSS_URL = 'https://api.rss2json.com/v1/api.json?rss_url=https://calculatedmoves.substack.com/feed';

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(RSS_URL);
        
        if (!response.ok) {
          throw new Error('Failed to fetch blog posts');
        }
        
        const data = await response.json();
        
        // Check if we have valid items
        if (data?.items?.length) {
          setPosts(data.items.slice(0, 3)); // Only get latest 3 posts
        } else {
          console.error('No posts found in feed');
          setPosts([]);
        }
      } catch (error) {
        console.error('Error loading recent posts:', error);
        setPosts([]); // Set empty state on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  if (isLoading) {
    return (
      <div className="bg-background-card rounded-[8px] p-6 shadow-lg">
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="bg-[#2A1114] rounded-[8px] p-6 shadow-lg mt-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Latest Blog Posts</h2>
          <a 
            href="https://calculatedmoves.substack.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white hover:opacity-80 text-sm"
          >
            View Blog
          </a>
        </div>
        <p className="text-gray-300 text-center">No posts available at the moment.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#2A1114] rounded-[8px] p-6 shadow-lg mt-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Latest Blog Posts</h2>
          <a 
            href="http://calculatedmoves.substack.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white hover:opacity-80 text-sm flex items-center"
          >
            View All
            <ChevronRight className="h-4 w-4 ml-1" />
          </a>
        </div>

        <div className="space-y-6">
          {posts.map((post, index) => (
            <a
              key={index}
              href={post.link || "http://calculatedmoves.substack.com"}
              target="_blank"
              rel="noopener noreferrer"
              className="block group"
            >
              <article className="flex space-x-4">
                <img
                  src={post.thumbnail || 'https://images.unsplash.com/photo-1497366811353-6870744d04b2'}
                  alt={post.title}
                  className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-medium mb-1 group-hover:opacity-80 transition-opacity line-clamp-2">
                    {post.title}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-white" />
                    <span className="text-sm text-gray-300">{formatDate(post.pubDate)}</span>
                  </div>
                </div>
              </article>
            </a>
          ))}
        </div>
    </div>
  );
}